import { CodeNameModel } from 'components/common/types';
import { Asset } from 'components/common/types/Asset.types';
import { CommonDeal, Line, Programmatic } from 'components/common/types/Deal.types';
import {
  DirectSales,
  DirectSalesLine,
  DirectSalesLockState,
  DirectSalesStatusOption,
  DirectSalesState,
} from 'components/common/types/DirectSalesCampaign.types';
import { VisibleAsset } from 'components/common/types/Planner.types';
import { FromTo } from 'components/common/types/Range.types';
import { ValueOf } from 'components/common/types/ValueOf';
import { SegmentFrameAverageByHour } from 'components/common/types/Segment.types';
import { CampaignViolation } from 'consts/campaignViolations';
import { CampaignType } from 'components/pages/Planner/PlannerSections/types';

export type AssetsView = 'AVAILABLE' | 'ALL' | 'SELECTED';

export type AllowedStateTransitionsMetadata = Record<string, DirectSalesStatusOption[]>;

export type BackupFormData = {
  commonDeal: CommonDeal;
  programmatic: Programmatic;
  lines: (Line | DirectSalesLine)[];
};

export const CampaignPlannerMode = {
  MAP: 'map',
  OVERVIEW: 'overview',
  REVIEW: 'review',
};

export type CampaignPlannerModeType = ValueOf<typeof CampaignPlannerMode>;

export type DealManagement = {
  backupFormData: BackupFormData;
  programmatic: Programmatic;
  directSales: DirectSales;
  directSalesSessionLock: DirectSalesLockState;
  commonDeal: CommonDeal;
  meta: DealManagementMeta;
  isNewDeal: boolean;
  isDisclaimerVisible: boolean;
  isDuplicated?: boolean;
  isEditingDisabled: boolean;
  loading: Partial<Record<Loading | LocalLoading, boolean>>;
  isLoading: boolean;
  isFetchingAllocation: boolean;
  isFetchingPricing: boolean;
  isFetchingReferences: boolean;
  isFetchingNetworks: boolean;
  isFetchingAdvancedAvailabilityReport: boolean;
  isFetchingProximityReport: boolean;
  isActionsDisabled: boolean;
  isForecastedAllocation: boolean;
  hasUnsavedProposal: boolean;
  isCpmCampaignLevel: boolean;
  selectedIndexOptimisationDataProvider: string;
  plannerSettings: PlannerSettings;
  plannerSecondaryPanelType: PlannerSecondaryPanelType;
  selectedEnvironmentChannel?: {
    dayPartGroupCode: string;
    audienceCategoryGroupCode: string;
  };
  temporaryDealId: string;
  disclaimer: DisclaimerObjectives;
  segmentFrameAverageByHour: SegmentFrameAverageByHour;
  campaignType: CampaignType;
  isCampaignDrawerOpen: boolean;
  campaignViolations: CampaignViolation[];
  isCampaignNameFocusRequested: boolean;
  campaignPlannerMode: CampaignPlannerModeType;
  isDspNoLongerInUseDialogVisible: boolean;
  isOOCFramesModalDismissed: boolean;
};

export type DealManagementMeta = {
  poi: {
    attributes: CodeNameModel[];
    datasources: CodeNameModel[];
  };
  productCategoryGroups: (Pick<CodeNameModel, 'code' | 'name'> & { productCategories?: CodeNameModel[] })[];
  allowedStateTransitions: AllowedStateTransitionsMetadata;
  originalLineStatuses: Record<string, DirectSalesState>;
};

export interface DisclaimerObjectives {
  success: string[];
  warning: string[];
  danger: string[];
  default?: string[];
}

export type DistributionOfAvailabilityAsset = Pick<Asset, 'frameId' | 'district' | 'productFormat'>;

export type FilteredAssets = Record<string, boolean>;

export type FrameIdIndex = {
  frameId: string;
  index: number;
};

export type ImpressionsMinMax = { min: number; max: number };

export enum Loading {
  GET_DEAL_DETAILS = 'GET_DEAL_DETAILS',
  GET_POI_DATA = 'GET_POI_DATA',
  GET_AVERAGE_SEGMENT_SCORES_PER_FRAME = 'GET_AVERAGE_SEGMENT_SCORES_PER_FRAME',
  GET_SEGMENT_FRAME_AVERAGE_BY_HOUR = 'GET_SEGMENT_FRAME_AVERAGE_BY_HOUR',
  GET_NETWORK_DATA = 'GET_NETWORK_DATA',
  GET_TAXONOMY_DATA = 'GET_TAXONOMY_DATA',
}

export enum LocalLoading {
  LOAD_ASSETS = 'LOAD_ASSETS',
  ADD_ASSET = 'ADD_ASSET',
  GET_REFERENCE_DATA = 'GET_REFERENCE_DATA',
}

export enum PlannerSecondaryPanelType {
  NONE = 'NONE',
  STATUS = 'STATUS',
  PRICING_MODE = 'PRICING_MODE',
  SCHEDULE = 'SCHEDULE',
  CHANNEL = 'CHANNEL',
  MEDIA_TYPE = 'MEDIA_TYPE',
  CPM = 'CPM',
  DSP = 'DSP',
  DUPLICATION = 'DUPLICATION',
  ENVIRONMENT = 'ENVIRONMENT',
  EXCLUSION_CRITERIA = 'EXCLUSION_CRITERIA',
  FORMAT = 'FORMAT',
  JUXTAPOSITION = 'JUXTAPOSITION',
  TAG = 'TAG',
  LOCATION = 'LOCATION',
  TARGETS = 'TARGETS',
  FRAME_LIST = 'FRAME_LIST',
  SOT = 'SOT',
  PROGRAMMATIC_STATUS = 'PROGRAMMATIC_STATUS',
  DIRECT_SALES_STATUS = 'DIRECT_SALES_STATUS',
  OBJECTIVE = 'OBJECTIVE',
  OBJECTIVE_LOCATIONS = 'OBJECTIVE_LOCATIONS',
  OBJECTIVE_FORMAT = 'OBJECTIVE_FORMAT',
  OBJECTIVE_POI = 'OBJECTIVE_POI',
  OBJECTIVE_TAGS = 'OBJECTIVE_TAGS',
  OBJECTIVE_FRAME_LIST = 'OBJECTIVE_FRAME_LIST',
  NETWORKS = 'NETWORKS',
  POI = 'POI',
  SEGMENTS = 'SEGMENTS',
  DAYPART_TARGETING = 'DAYPART_TARGETING',
  PRODUCT_TEMPLATE = 'PRODUCT_TEMPLATE',
  VENUE_TAXONOMY = 'VENUE_TAXONOMY',
  CREATIVE_TYPE = 'CREATIVE_TYPE',
  LIVE_LINE_SPLIT = 'LIVE_LINE_SPLIT',
  MEDIA_OWNER = 'MEDIA_OWNER',
  VISUAL_UNITS = 'VISUAL_UNITS',
  INDEX_OPTIMISATION = 'INDEX_OPTIMISATION',
  NON_MEDIA_SERVICES = 'NON_MEDIA_SERVICES',
  FRAME_CPM = 'FRAME_CPM',
  BILLING_RULES = 'BILLING_RULES',
  MULTI_TARGETING = 'MULTI_TARGETING',
}

export type PlannerSettings = {
  assetsView: AssetsView;
  isImpressionsClusterVisible: boolean;
  isAvailabilityClusterVisible: boolean;
  isAllocationClusterVisible: boolean;
  impressions: ImpressionsMinMax;
  enabledImpressionsRange: FromTo[];
  assetsOnViewport: VisibleAsset[];
  isAvailabilityClusterToggleDisabled: boolean;
  isAllocationClusterToggleDisabled: boolean;
  zoom: number;
  frameIdIndexes: FrameIdIndex[];
  selectedIndexRange?: FromTo;
  isSummaryPanelOpen: boolean;
  isSimplifiedPlanningMode: boolean;
  dealLineSectionOpenState: {
    info?: boolean;
    filters?: boolean;
    targets?: boolean;
    services?: boolean;
    CPMPricing?: boolean;
    billingRule?: boolean;
  };
  isClientSideAssetFiltering?: boolean;
  filteredAssetsIds: FilteredAssets;
  hoveredFurnitureCode: string;
  isFullAvailabilityRequested: boolean;
};
